import MapLayout from "./MapLayout";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  CanadaMapSourceLayer,
  CanadaRestrictedMapBbox,
  BothCountriesMapBbox,
  centerOfCanada,
  centerOfUSA,
  centerOfBothCountries,
  DefaultValues,
  DisplayErrorMessages,
  mapFeatureLayer,
  mapRoadLayer,
  MapSourceLayer,
  MaxImageCount,
  RestrictedMapBbox,
  centerOfMap,
} from "../../utils/helper";
import { palette, themeWidth } from "../../themes/theme";
import {
  htmlToJpegConvert,
  htmlToPdfConvert,
  htmlToPngConvert,
  htmlToSvgConvert,
} from "../../utils/imgConverter";
import { MarkerStyleSetting } from "../../component/MapSettings/MarkerStyleSettings";
import { MapToolsBox } from "./MapToolsBox";
import { PolygonStyleSetting } from "../../component/MapSettings/PolygonStyleSettings";
import { LogoStyleSetting } from "../../component/MapSettings/LogoStyleSetting";
import { LogoSetting } from "../../component/MapSettings/LogoSetting";
import { MapToolboxContext } from "../../context/mapToolbox/mapToolbox.provider";
import { ShapeSelector } from "../../component/MapSettings/ShapeSelector";
import { ShapeStyleSetting } from "../../component/MapSettings/ShapeStyleSetting";
import { TextStyleSetting } from "../../component/MapSettings/TextStyleSetting";
import { CustomShape } from "../../component/MapSettings/utilities/CustomShape";
import { CustomPolygonClass } from "../../component/MapSettings/utilities/CustomPolygon";
import { CustomTextBoxClass } from "../../component/MapSettings/utilities/CustomTextbox";
import { showLoader } from "../../redux/slices/loaderSlice";
import {
  createUserMap,
  fetchLocationDetails,
  getUserMap,
  postRefreshToken,
} from "../../api";
import {
  addSelectedLayer,
  clearTradeArea,
  createTradeArea,
  resetMapSettings,
  setActiveMarker,
  setMapSaving,
  setMapStyle,
  setPoiSettings,
  setSatelliteOption,
  setSaveAsMapInstance,
  setSavedMap,
  setSaveMapInstance,
  setShowAgsReport,
  showPoiLoader,
} from "../../redux/slices/mapSlice";
import { CustomPOIClass } from "../../component/MapSettings/utilities/CustomPOI";
import { useNavigate, useParams } from "react-router-dom";
import { CommonUtils } from "../../component/MapSettings/utilities/CommonUtils";
import { setPoiSetting } from "../../redux/slices/mapSettingsSlice";
import { CustomMarkerClass } from "../../component/MapSettings/utilities/CustomMarker";
import SaveMapModal from "../../component/Modal/SaveMapModal";
import { SaveMapScreen } from "../../api/extraService";
import MapStyleSetting from "../../component/MapSettings/MapStyleSetting";
import { AgsReport } from "../../component/MapSettings/AgsReportSettings";
import { getAgsReportList } from "../../redux/thunk/mapThunk";
import { BulbSetting } from "../../component/MapSettings/BulbSetting";
import { AllPOIStyleSettings } from "../../component/MapSettings/AllPOISettings";
import MapHeader from "./MapHeader";
import HighwaySetting, {
  AddHighWayLayer,
} from "../../component/MapSettings/HighwaySetting";
import { showToaster } from "../../redux/slices/toastSlice";
import CrimeRiskLegend from "./CrimeRiskLegend";

mapboxgl.accessToken = process.env.REACT_APP_MAP_BOX_API_KEY;

const defaultMapToolBoxState = {
  polygon: false,
  logoUpload: false,
  textSetting: false,
  shape: false,
};

const BuildAMap = () => {
  // --------------------- CUSTOM / OTHER HOOKS ---------------------
  const mapState = useSelector((state) => state.map);
  const activeMapStyle = useSelector((state) => state.map.mapStyle);
  const shapeStyleSettings = useSelector(
    (state) => state.mapSettings.shapeSetting
  );
  const textBoxStyleSettings = useSelector(
    (state) => state.mapSettings.textBoxSetting
  );
  const poiStyleSettings = useSelector((state) => state.mapSettings.poiSetting);
  const highwayStyleSettings = useSelector(
    (state) => state.mapSettings.highwaySetting
  );
  const logoStyleSettings = useSelector(
    (state) => state.mapSettings.logoSetting
  );
  const locationTradeAreaSettings = useSelector(
    (state) => state.mapSettings.locationTradeAreaSetting
  );
  const activeMarker = useSelector((state) =>
    state.map.tradeAreas.find((d) => d.id === state.map.activeMarker)
  );
  const activeAgsReport = useSelector((state) =>
    state.map.tradeAreas.find((d) => d.id === state.map.showAgsReport)
  );
  const user = useSelector((state) => state.user);
  const shadingSettings = useSelector((state) => state.map.shadingSettings);

  const { activeId, setActiveId, activeIdRef } = useContext(MapToolboxContext);
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  // --------------------- REFS ---------------------
  const mapWrapperRef = useRef();

  // Initialize countryNameRef based on user's selected countries
  // This must be computed early but can't use useMemo due to hook order
  const getInitialCountryName = () => {
    const selectedCountries = user.adminData?.selectedCountries || ["usa"];
    const hasUSA = selectedCountries.includes("usa");
    const hasCanada = selectedCountries.includes("canada");
    const canToggle = hasUSA && hasCanada;

    if (!canToggle && hasCanada && !hasUSA) return "Canada";
    if (!canToggle && hasUSA) return "USA";
    // For canToggle case, default to Canada (matches countryToggle initial state of false)
    return "Canada";
  };

  const countryNameRef = useRef(getInitialCountryName());
  const mapRef = useRef();
  const leagendRef = useRef();
  const highlightAreaRef = useRef();
  const drawRef = useRef();
  const currentModeRef = useRef("simple_select");
  const lastClickButtonRef = useRef(0);
  const isEnabledActionRef = useRef("textSetting");
  const poiSettingRef = useRef(poiStyleSettings);
  const defaultLogoValueRef = useRef({
    id: "",
    connectedLeaderLineId: "",
    openSetting: false,
    resetLogoSettings: false,
    polyLineColor: logoStyleSettings.logoLeaderLineColor,
    polyLineWidth: logoStyleSettings.logoLeaderLineBorderWidth,
    imageBackgroundColor: logoStyleSettings.logoBackgroundColor,
    isBackgroundColorTransparent: false,
    image: "",
    logoHaloEffect: logoStyleSettings.logoHaloEffect,
    logoHaloEffect_x: 0,
    logoHaloEffect_y: 0,
    logoHaloEffect_b: 0,
    logoHaloEffect_c: 0,
    isHaloEffectTransparent: false,
    isLeaderLineEnabled: true,
    isLeaderLineDisabled: false,
    width: "",
    height: "",
    lat: "",
    lng: "",
  });
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const requestQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const reconnectDelayRef = useRef(1000);
  const allCallOutMarkerRef = useRef(null);
  const markerLocationCalculatedRef = useRef({});
  const showPointerRef = useRef(false);
  const isLoadSavedMapRef = useRef(false);
  const isClusterCallOutRef = useRef(false);
  const markerRef = useRef([]);
  const deletedPoiRef = useRef([]);
  const mapActionsRef = useRef();
  const activeLayerRef = useRef({
    name: "continents",
    colorScheme: shadingSettings.legendColorSchemeData,
    opacity: shadingSettings.opacity,
  });
  const activeTypeRef = useRef({
    state: "crime",
    type: mapState.activeCrimeType,
  });

  // --------------------- STATES ---------------------
  const [mapRState, setMapRState] = useState();
  const [showOpacityControl, setShowOpacityControl] = useState(false);
  const [mapLayoutState, setMapLayoutState] = useState();
  const [markers, setMarkers] = useState([]);
  const [mapCoordinates, setMapCoordinates] = useState({ lat: 0, lng: 0 });
  // const [showPolygonStyleSettings, setShowPolygonStyleSettings] =
  //   useState(false);
  const [polygonSelected, setPolygonSelected] = useState(null);
  // const [shapeSelected, setShapeSelected] = useState(null);
  // const [textSelected, setTextSelected] = useState(null);
  const [mapToolBarOptionFlags, setMapToolBarOptionFlags] = useState(
    defaultMapToolBoxState
  );
  const [showLogoSetting, setShowLogoSetting] = useState(null);
  const [selectedLogoSettings, setSelectedLogo] = useState(null);
  const [mapRoadLayerState, setMapRoadLayerState] = useState(
    JSON.parse(JSON.stringify(mapRoadLayer))
  );
  const [mapFeatureLayerState, setMapFeatureLayerState] = useState(
    JSON.parse(JSON.stringify(mapFeatureLayer))
  );
  const [shapeSettings, setShapeSettings] = useState({
    ...DefaultValues.defaultShapeSetting,
  });

  // Get user's selected countries from adminData
  const selectedCountries = user.adminData?.selectedCountries || ["usa"];
  const hasUSA = selectedCountries.includes("usa");
  const hasCanada = selectedCountries.includes("canada");
  const canToggle = hasUSA && hasCanada; // Can toggle only if both countries available

  // on true default city will be USA else Canada
  // Initialize based on available countries
  const [countryToggle, setCountryToggle] = useState(() => {
    if (canToggle) {
      return true;
    }
    return hasUSA; // true for USA, false for Canada
  });
  const [exportSettingsData, setExportSettingsData] = useState({
    height: "100%",
    width: "60%",
    x: "calc((100% - 60%) / 2)",
    y: "calc((100% - 100%) / 2)",
    size: "large",
    orientation: "portrait",
    format: "png",
    dpi: 400,
  });
  const [textBox, setTextBox] = useState({
    textBoxInstance: {},
    selectedTextBox: null,
    addTextBoxStyle: null,
    textBoxMoveInstance: null,
  });
  const [POICategoryList, setPOICategoryList] = useState({
    list: {},
    count: {},
    loader: 0,
    selectedAsLayer: [],
    triggerPoiCheck: 0,
    triggerPoiRemove: 0,
    triggerAddPoiCheck: 0,
    selectedPOIRetailerNameList: {},
    unProcessedPoiList: [],
    filtersList: [],
    selectedFilters: [],
    triggerPoiListReload: 0,
    triggerPoiLayerCheck: 0,
    currentZoomLevel: mapState?.mapZoom,
    processedList: [],
    triggerFromFilter: false,
    triggerGetPoi: false,
    isZoom: false,
    selectedPOICount: 0,
    updatePoiFilter: 0,
    latestFilter: {
      filterId: "",
      retailerId: [],
    },
  });
  // const [quickGuidePOIList, setQuickGuidePOIList] = useState({
  //   list: {},
  //   count: {},
  // });
  const [userMap, setUserMap] = useState({
    folderList: [],
    folderName: null,
    folderId: null,
    mapName: null,
    mapId: null,
    autoSavingMap: false,
    saveMapFolderListSelect: true,
    isMapChanged: false,
  });
  const [selectedRoadLayers, setSelectedRoadLayers] = useState([]);

  // --------------------- API FUNCTIONS ---------------------
  const processNextRequest = (type) => {
    if (isProcessingRef.current || requestQueueRef.current.length === 0) {
      return;
    }

    const nextRequest = requestQueueRef.current.shift();
    if (wsRef && wsRef.current && nextRequest) {
      isProcessingRef.current = true;
      wsRef.current.send(JSON.stringify(nextRequest));
      if (type === "list-poi") {
        dispatch(showPoiLoader(true));
      }
    }
  };

  const sendWebSocketMessage = (type, message) => {
    if (wsRef && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Check if a request of the same type exists in the queue
      const existingIndex = requestQueueRef.current.findIndex(
        (req) => req.type === type
      );

      if (existingIndex !== -1) {
        // Replace the existing request with the new one
        requestQueueRef.current[existingIndex] = { type, message };
      } else {
        // Add new request to the queue
        requestQueueRef.current.push({ type, message });
      }

      // Start processing the queue
      processNextRequest(type);
    }
    // else if (!wsRef?.current) {
    //   const token = localStorage.getItem("accessToken");
    //   if (token) {
    //     getWebSocketConnection(token);
    //     setTimeout(() => {
    //       sendWebSocketMessage(type, message);
    //     }, 1000);
    //   }
    // }
  };

  // --------------------- USE CALLBACK  ---------------------
  const getChangedLocationImages = useCallback(() => {
    if (mapRef.current) {
      const getBound = mapRef.current.getBounds();
      const getZoom = mapRef.current.getZoom();
      if (getZoom > 8) {
        if (!POICategoryList.triggerFromFilter) {
          sendWebSocketMessage("list-poi", {
            data: {
              type: "list_poi",
              bounding_box: {
                top_left_lat: getBound.getNorthWest().lat,
                top_left_long: getBound.getNorthWest().lng,
                bottom_right_lat: getBound.getSouthEast().lat,
                bottom_right_long: getBound.getSouthEast().lng,
              },
            },
          });
        }
        setPOICategoryList((prev) => ({
          ...prev,
          triggerFromFilter: false,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [POICategoryList]);

  const handleWebSocketMessage = useCallback(
    (type, message) => {
      if (type === "quick-guide-get-poi") {
        // setQuickGuideSelectedPOI((prev) => {
        //   return [...message.data];
        // });
        setPOICategoryList((previousValue) => ({
          ...previousValue,
          loader: previousValue.loader - 1,
        }));
        dispatch(showLoader(false));
      } else if (type === "quick-guide-list-poi") {
        setPOICategoryList((previousValue) => ({
          ...previousValue,
          loader: previousValue.loader - 1,
        }));
        // setQuickGuidePOIList({
        //   list: message.data.list,
        //   count: message.data.count,
        // });
        dispatch(showLoader(false));
      } else {
        setPOICategoryList((previousValue) => {
          if (type === "list-poi") {
            const selectedPoiRetailer = Object.keys(
              previousValue.selectedPOIRetailerNameList
            );
            const totalValue = selectedPoiRetailer.reduce((sum, key) => {
              if (
                message.data.count.hasOwnProperty(key) &&
                !previousValue.selectedAsLayer.includes(key)
              ) {
                return sum + message.data.count[key];
              }
              return sum;
            }, 0);
            return {
              ...previousValue,
              list: message.data.list,
              count: message.data.count,
              loader: previousValue.loader - 1,
              triggerGetPoi: totalValue > MaxImageCount ? false : true,
              triggerPoiLayerCheck:
                totalValue > MaxImageCount
                  ? previousValue.triggerAddPoiCheck + 1
                  : previousValue.triggerAddPoiCheck,
            };
          } else if (type === "get-poi") {
            const newProcessedList = [
              ...previousValue.processedList,
              ...message.data.filter(
                (newItem) =>
                  !previousValue.processedList.some(
                    (existingItem) => existingItem._id === newItem._id
                  )
              ),
            ];
            return {
              ...previousValue,
              unProcessedPoiList: message.data,
              processedList: newProcessedList,
              triggerAddPoiCheck: previousValue.triggerAddPoiCheck + 1,
              loader: previousValue.loader - 1,
              triggerGetPoi: false,
              isZoom: false,
            };
          }
          previousValue.loader = previousValue.loader - 1;
          return previousValue;
        });
      }
      dispatch(showPoiLoader(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // dispatch,
      mapState.tradeAreas,
      // updateAutoSave,
      POICategoryList,
    ]
  );

  const startHeartbeat = useCallback(() => {
    heartbeatRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000); // Ping every 30 seconds
  }, []);

  const refreshTokenAndReconnect = useCallback(async () => {
    try {
      const payloadRefreshToken = localStorage.getItem("refreshToken");
      if (!payloadRefreshToken) return;
      const res = await postRefreshToken("auth/refreshToken", {
        refreshToken: payloadRefreshToken,
        isCrimeRisk: true,
      });
      if (!res?.data) return;
      const { refreshToken, accessToken } = res.data;
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      // getWebSocketConnection(accessToken);
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryConnection = useCallback(() => {
    setTimeout(() => {
      reconnectDelayRef.current = Math.min(
        reconnectDelayRef.current * 2,
        30000
      ); // Exponential backoff, max 30s
      const token = localStorage.getItem("accessToken");
      if (token) {
        getWebSocketConnection(token);
      }
    }, reconnectDelayRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------- FUNCTIONS ---------------------
  const getWebSocketConnection = (token) => {
    if (!wsRef.current && token) {
      wsRef.current = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL, token);

      wsRef.current.onopen = () => {
        startHeartbeat();
      };

      wsRef.current.onmessage = (message) => {
        if (message.data) {
          const socketResponse = JSON.parse(message.data);
          if (socketResponse && socketResponse.success) {
            handleWebSocketMessage(socketResponse.type, socketResponse);
          }
          isProcessingRef.current = false;
          processNextRequest(null);
        }
      };

      wsRef.current.onclose = (error) => {
        if (error.reason === "Invalid token" || error?.code === 1006) {
          refreshTokenAndReconnect();
        }
        // else if ([1001, 1012, 1013, 1014].includes(error.code)) {
        //   retryConnection();
        // }
        wsRef.current = null;
      };

      wsRef.current.onerror = (error) => {
        console.log("error", error);
        wsRef.current = null;
        // retryConnection();
      };
    }
  };

  const resetMapToolbarOptionFlags = () => {
    setMapToolBarOptionFlags(defaultMapToolBoxState);
  };

  const resetActiveId = () => {
    resetMapToolbarOptionFlags();
    mapRef.current.getCanvas().style.cursor = "grab";
    setActiveId("");
  };

  const disableUserActions = useCallback(() => {
    if (mapActionsRef.current) return true;
    return false;
  }, []);

  const handleLogoSettingClose = () => {
    setMapToolBarOptionFlags((d) => ({ ...d, logoUpload: false }));
    setShowLogoSetting(false);
    resetActiveId();
  };

  const resetAllOnceChanged = () => {
    lastClickButtonRef.current = 0;
    if (mapRef.current) {
      mapRef.current.getCanvas().style.cursor = "grab";
    }
    //TODO: Temporarily added them here.
    resetMapToolbarOptionFlags();
    dispatch(setActiveMarker(null));
    //-------------------------
    setShowLogoSetting(false);
    setSelectedLogo(null);
    setTextBox((previousValue) => {
      return { ...previousValue, selectedTextBox: null };
    });
    if (drawRef.current) {
      drawRef.current.changeMode("simple_select");
    }
    setShapeSettings((previousValue) => {
      previousValue.addSelectedShapeFlag = null;
      previousValue.addShapeToMapFlag = false;
      previousValue.selectedShape = null;
      return previousValue;
    });
    dispatch(setPoiSettings(false));
  };

  const resetPolygonDrawOnceChanged = () => {
    resetAllOnceChanged();
    setShapeSettings({ ...DefaultValues.defaultShapeSetting });
  };

  const base64toBlob = (dataUrl) => {
    const [meta, content] = dataUrl.split(",");
    const mime = meta.match(/:(.*?);/)[1];
    const byteString = atob(content);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uintArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([uintArray], { type: mime });
  };

  const exportChanges = async () => {
    dispatch(
      showLoader({
        showLoader: true,
        loaderMessage: "Loading...",
        showBackDrop: true,
      })
    );
    switch (exportSettingsData.format) {
      case "png":
        let leagendUrl = "";

        if (mapState.activeCrimeType || mapState.activeEnvType) {
          const leagendRes = await htmlToPngConvert(
            leagendRef.current,
            null,
            false,
            leagendRef.current
          );
          const leagendBlob = base64toBlob(leagendRes);
          leagendUrl = URL.createObjectURL(leagendBlob);
          if (leagendRef.current) {
            leagendRef.current.style.display = "none";
          }
        }

        const res = await htmlToPngConvert(
          mapWrapperRef.current,
          exportSettingsData.orientation,
          false,
          highlightAreaRef.current
        );

        console.log(leagendUrl, "leagend Res");
        const blob = base64toBlob(res);
        const objectUrl = URL.createObjectURL(blob);
        if (mapState.activeCrimeType || mapState.activeEnvType) {
          if (leagendRef.current) {
            leagendRef.current.style.display = ""; // or "block" depending on layout
          }
        }
        const url = `/edit-image?dataUrl=${objectUrl}&orientation=${exportSettingsData.orientation}&leagendUrl=${leagendUrl}`;
        window.open(url, "_blank");
        // navigate("/edit-image", { state: { dataUrl: objectUrl } });
        break;
      case "jpeg":
        await htmlToJpegConvert(
          mapWrapperRef.current,
          exportSettingsData.orientation,
          true,
          highlightAreaRef.current
        );
        break;
      case "svg":
        await htmlToSvgConvert(
          mapWrapperRef.current,
          exportSettingsData.orientation,
          true,
          highlightAreaRef.current
        );
        break;
      case "pdf":
        await htmlToPdfConvert(
          mapWrapperRef.current,
          exportSettingsData.orientation,
          true,
          highlightAreaRef.current
        );
        break;

      default:
        break;
    }
    dispatch(showLoader(false));
  };

  const handleMapClickEvent = (event) => {
    setMapCoordinates(event.lngLat);

    if (
      activeIdRef.current === "arrow" &&
      ((event.originalEvent.button === 0 &&
        event.originalEvent.type === "click") ||
        event.originalEvent.type === "touchend")
    ) {
      const coordinates = event.lngLat;
      const mouseX = event.originalEvent.clientX;
      const mouseY = event.originalEvent.clientY;
      const popupClass = [
        "add-point-popup",
        mouseY < 150
          ? mouseX < 500 && true // TODO: replace true with showDrawerRef.current
            ? ""
            : "bottom-0"
          : "",
        mouseX < 500 && true // TODO: replace true with showDrawerRef.current
          ? "change-popup-pos mapboxgl-popup-anchor-left"
          : "",
      ]
        .filter(Boolean)
        .join(" ");

      const popup = new mapboxgl.Popup({
        className: popupClass,
        closeButton: false,
      })
        .setLngLat(coordinates)
        .setHTML(
          `
                <div class='addpoint-sec'>
                <label style="color:#00000080;">Add to location & trade area</label>
                <div style="display:flex;gap:12px;margin-top:13px;">
                    <button class='map-btn-light-outline' id="add-location">Yes</button>
                    <button class='map-btn-light-outline' id="close-popup">No</button>
                </div>
                </div>
            `
        )
        .addTo(mapRef.current);
      // popupRef.current = popup;
      const popupElement = popup.getElement();

      // TODO: replace true with showDrawerRef.current
      if (mouseX < 500 && true && popupElement) {
        popupElement.style.left = "90px";
        popupElement.style.top = mouseY < 945 ? "45px" : "0px";
      }

      const yesButton = document.getElementById("add-location");
      const noButton = document.getElementById("close-popup");

      if (yesButton) {
        yesButton.addEventListener("click", async () => {
          yesButton.classList.add("disabled-pointer");
          if (mapState.tradeAreas.length < 100) {
            const locationName = await fetchLocationDetails({
              ...coordinates,
              accessToken: mapboxgl.accessToken,
            });

            const id = `${coordinates.lng} ${coordinates.lat} ${locationName}`;
            const checkDuplicate = mapState.tradeAreas.filter(
              (item) => item.id === id
            );

            if (checkDuplicate.length === 0) {
              CustomMarkerClass.AddMarkerToPolygon({
                id,
                coordinates: {
                  longitude: coordinates.lng,
                  latitude: coordinates.lat,
                },
                type: "trade-area",
                mapRef: mapRef.current,
                drawRef: drawRef.current,
                dispatch,
                name: locationName,
                radiusSetting: locationTradeAreaSettings.radiusSetting,
                resetAllOnceChanged,
                lastClickButton: lastClickButtonRef,
                resetActiveId,
                markerRef,
              });
              setMarkers(markerRef.current);
            }
          }
          setActiveId("");
          popup.remove();
        });
      }

      if (noButton || activeIdRef.current !== "arrow") {
        setActiveId("");
        noButton?.addEventListener("click", () => {
          popup.remove();
        });
      }
      mapRef.current.getCanvas().style.cursor = "grab";
    }
  };

  const createPolygon = () => {
    if (!drawRef.current) {
      CustomPolygonClass.CreatePolygon({
        currentModeRef,
        drawRef,
        lastClickButtonRef,
        mapRef: mapRef.current,
        resetAllOnceChanged,
        setActiveId,
        setMapToolBarOptionFlags,
        setPolygonSelected,
        isClusterCallOut: isClusterCallOutRef,
        createNewPolygon: createPolygon,
        logoDefaultStyle: logoStyleSettings,
        defaultLogoValue: defaultLogoValueRef,
        poiSetting: poiStyleSettings,
        setSelectedLogo,
        setMarkers,
        getCurrentPoiSetting,
        markerRef,
        disableUserActions,
      });
    } else {
      drawRef.current.changeMode("draw_polygon");
    }
  };

  const createTextBox = async ({
    coordinates,
    previousValue,
    markerId,
    defaultText = "",
    currentMarkerId = "",
    textBoxDefaultStyle = textBoxStyleSettings,
  }) => {
    createPolygon();
    await CustomTextBoxClass.CreateTextBox({
      coordinates,
      mapboxgl,
      map: mapRef.current,
      mapDraw: drawRef.current,
      resetAllOnceChanged,
      setTextBox,
      setActiveId,
      textBoxDefaultStyle,
      lastClickButton: lastClickButtonRef,
      // updateAutoSave,
      currentMode: currentModeRef,
      polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
      isEnabledAction: isEnabledActionRef.current,
      disableUserActions,
      defaultText,
      currentMarkerId,
      // setUndoHistory,
      // setRedoHistory,
      setDrawnFeaturesSupport: () => {},
      isCreate: true,
      previousValue,
      markerId,
      markerRef,
    });
    setMarkers(markerRef.current);
  };

  const openSettingsMenu = async (mapCoordinates) => {
    if (mapToolBarOptionFlags.logoUpload) {
      setShowLogoSetting(true);
      mapRef.current.getCanvas().style.cursor = "pointer";
      return;
    }
    if (activeId === "shape" && shapeSettings.addSelectedShapeFlag) {
      setMapToolBarOptionFlags((d) => ({ ...d, shape: true }));
      // setShapeSelected(true);
      mapRef.current.getCanvas().style.cursor = "default";
      setActiveId("");
      CustomShape.AddShapeToMap({
        coordinates: mapCoordinates,
        shapeType: shapeSettings.addSelectedShapeFlag,
        mapboxgl,
        map: mapRef.current,
        lastClickButton: lastClickButtonRef,
        shapeStyleSettings,
        resetAllOnceChanged,
        // updateAutoSave,
        setShapeSettings,
        setMapToolBarOptionFlags,
        disableUserActions,
        markerRef,
      });
      setMarkers(markerRef.current);
      return;
    }

    if (activeId === "add-text") {
      // setResetMap(false);
      isEnabledActionRef.current = "textSetting";
      await createTextBox({ coordinates: mapCoordinates });
      // setTextSelected(true);
      mapRef.current.getCanvas().style.cursor = "default";
      setActiveId("");
      return;
    }
  };

  const getCurrentPoiSetting = () => {
    return poiSettingRef.current;
  };

  const getMapStyle = (selectedLayers) => {
    const layers = mapRef.current.getStyle().layers;
    const styleName = mapRef.current.getStyle().name;
    const selectedMapLayers =
      selectedLayers.length > 0 ? selectedLayers : selectedRoadLayers;

    const showAllOtherLayers = selectedMapLayers.includes("all-other-layers");
    const showAllRoadLayers = selectedMapLayers.includes("all-road-layers");

    // Road Layers list
    const additionalRoadLayer = JSON.parse(JSON.stringify(mapRoadLayer));
    additionalRoadLayer.forEach((layerGroup) => {
      if (layerGroup.title !== "Highways") {
        layerGroup.disable = true;
      }
      layerGroup.values.forEach((value) => {
        const matchingLayer = layers.find((l) => l.id === value.id);
        value.disable = true;
        if (matchingLayer) {
          value.disable = false;
          layerGroup.disable = false;
        }
      });
    });
    // setRoadLayers(additionalRoadLayer);

    // Feature Layers list
    const additionalFeatureLayer = JSON.parse(JSON.stringify(mapFeatureLayer));
    mapFeatureLayer.forEach((layerGroup) => {
      layerGroup.disable = true;
      layerGroup.values.forEach((value) => {
        const matchingLayer = layers.find((l) => l.id === value.id);
        value.disable = true;
        if (matchingLayer) {
          value.disable = false;
          layerGroup.disable = false;
        }
        const styleNames = ["Mapbox Navigation Day", "Mapbox Navigation Night"];
        const valueIds = [
          "land-structure-line",
          "land-structure-polygon",
          "national-park",
          "waterway",
          "building",
        ];
        const walkingLayers = ["road-path", "road-pedestrian"];
        if (
          (styleNames.includes(styleName) && valueIds.includes(value.id)) ||
          (styleName === "Mapbox Satellite Streets" &&
            walkingLayers.includes(value.id))
        ) {
          value.disable = true;
          layerGroup.disable = true;
        }
      });
    });
    // setLayers(additionalFeatureLayer);
    additionalRoadLayer.forEach((item) => {
      item.values.forEach((layer) => {
        if (!layer.disable && !selectedMapLayers.includes(layer.id)) {
          mapRef.current.setLayoutProperty(layer.id, "visibility", "none");
        }
        if (selectedLayers.length < 0)
          mapRef.current.setLayoutProperty(
            layer.id,
            "visibility",
            showAllRoadLayers
              ? "visible"
              : selectedMapLayers.includes(layer.id)
              ? "visible"
              : "none"
          );
      });
    });
    additionalFeatureLayer.forEach((item) => {
      item.values.forEach((layer) => {
        if (!layer.disable && !selectedMapLayers.includes(layer.id)) {
          mapRef.current.setLayoutProperty(layer.id, "visibility", "none");
        }
        if (selectedLayers.length < 0)
          mapRef.current.setLayoutProperty(
            layer.id,
            "visibility",
            showAllOtherLayers
              ? "visible"
              : selectedMapLayers.includes(layer.id)
              ? "visible"
              : "none"
          );
      });
    });
    if (mapRef.current.getLayer("road-motorway-trunk")) {
      mapRef.current.setLayoutProperty(
        "road-motorway-trunk",
        "visibility",
        "none"
      );
    }
  };

  const getCurrentMapData = async (mapId) => {
    dispatch(
      showLoader({
        showLoader: true,
        loaderMessage: "Loading...",
        showBackDrop: true,
      })
    );
    const res = await getUserMap({ id: user.adminData.id + "/" + mapId });
    if (!res) {
      dispatch(showLoader(false));
      return;
    }

    const userMapData = res.data;
    CommonUtils.ApplyToMap({
      map: mapRef.current,
      mapData: userMapData.mapData,
      getMapStyle,
      dispatch,
    });
    setPOICategoryList((prev) => ({
      ...userMapData.mapData.poi,
      loader: prev.loader,
    }));
    dispatch(
      setPoiSetting(userMapData.mapData.poiSettings ?? poiStyleSettings)
    );
    mapRef.current.once("idle", () => {
      dispatch(
        showLoader({
          showLoader: true,
          loaderMessage: "Loading...",
          showBackDrop: true,
        })
      );
      // Apply polygon with markers
      CommonUtils.RemoveMapBoxDrawLayersAndSources(mapRef.current).then(() => {
        createPolygon();
        setSelectedRoadLayers(
          userMapData.mapData.mapLayer ? userMapData.mapData.mapLayer : []
        );
        dispatch(
          addSelectedLayer(
            userMapData.mapData.mapLayer ? userMapData.mapData.mapLayer : []
          )
        );
        CommonUtils.ApplyDrawnFeaturesToMap({
          mapDraw: drawRef.current,
          mapData: userMapData.mapData,
          setActiveId,
          setDrawnFeaturesSupport: () => {},
        });

        CommonUtils.GeoJSONToMarkers({
          markers: userMapData.mapData.markers,
          mapboxgl,
          map: mapRef.current,
          mapDraw: drawRef,
          resetAllOnceChanged,
          setTextBox,
          lastClickButton: lastClickButtonRef,
          logoDefaultStyle: logoStyleSettings,
          updateAutoSave: () => {},
          setSelectedLogo,
          textBoxDefaultStyle: textBoxStyleSettings,
          dispatch,
          setShapeSettings,
          setMapToolBarOptionFlags,
          setResetMap: () => {},
          createNewPolygon: createPolygon,
          currentMode: currentModeRef,
          defaultLogoValue: defaultLogoValueRef,
          poiSetting: poiStyleSettings,
          polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
          setPointerSelected: () => {},
          getCurrentPoiSetting,
          isMapExport: disableUserActions,
          setDrawnFeaturesSupport: () => {},
          setUndoHistory: () => {},
          setRedoHistory: () => {},
          showPointer: showPointerRef,
          markerRef,
          locationTradeAreaSettings,
        });
        setMarkers(markerRef.current);
        dispatch(createTradeArea(userMapData.mapData.tradeAreaList));
        // CommonUtils.ApplyAdditionalLayers({
        //   map: mapRef.current,
        //   setHighways,
        //   setHighwaySelected,
        //   setPointOfInterest,
        //   setShowLayerAccOpen,
        // });
        const currentMapStyle = userMapData.mapData.style?.sprite;
        if (currentMapStyle) {
          const lastPart = currentMapStyle.split("/").pop();
          dispatch(setMapStyle(lastPart));
          if (["satellite-streets-v12"].includes(lastPart)) {
            dispatch(setSatelliteOption(true));
          } else {
            dispatch(setSatelliteOption(false));
          }
        }
        mapRef.current.once("idle", () => {
          isLoadSavedMapRef.current = false;
          dispatch(showLoader(false));
        });
      });
    });
    const folderDetails = userMap.folderList.filter(
      (folder) => folder.id === userMapData.folderId
    );
    setUserMap((previousValue) => ({
      ...previousValue,
      folderId: userMapData.folderId,
      mapId: userMapData.id,
      mapName: userMapData.name,
      folderName: userMapData.folderName,
      // ...(folderDetails &&
      //   folderDetails.length && {
      //     folderName: folderDetails[0].name,
      //   }),
    }));
    dispatch(
      setSavedMap({
        isMapSaved: true,
        savedMapName: userMapData.name,
        ...(folderDetails &&
          folderDetails.length && {
            savedMapFolderName: folderDetails[0].name,
          }),
      })
    );
    isLoadSavedMapRef.current = false;
    // callAutoSave();
  };

  const saveMapInstanceToAPI = async (payload) => {
    try {
      dispatch(
        showLoader({
          showLoader: true,
          showBackDrop: true,
        })
      );
      dispatch(setMapSaving(true));

      const mapData = {
        center: mapRef.current.getCenter(),
        zoom: mapRef.current.getZoom(),
        bearing: mapRef.current.getBearing(),
        pitch: mapRef.current.getPitch(),
        style: mapRef.current.getStyle(),
        markers: CommonUtils.MarkersToGeoJSON(markers),
        polygons: drawRef.current?.getAll(),
        tradeAreaList: mapState.tradeAreas,
        mapLayer: selectedRoadLayers,
        poi: {
          ...POICategoryList,
          unProcessedPoiList: [],
          processedList: [],
        },
        poiSettings: poiStyleSettings,
      };

      let mapScreen;
      if (!payload.mapId) mapScreen = await SaveMapScreen(dispatch);

      const res = await createUserMap({
        body: {
          ...(payload.folderId !== "" && {
            folderId: payload.folderId,
          }),
          ...(payload.folderName !== "" && {
            folderName: payload.folderName,
          }),
          ...(payload.mapId && {
            mapId: payload.mapId,
          }),
          ...(payload.mapName !== "" && {
            mapName: payload.mapName,
          }),
          ...(mapScreen && { screenShot: mapScreen }),
          mapData: mapData,
          userId: user.adminData.id,
        },
      });

      if (!res?.data) return;

      setUserMap((previousValue) => {
        const previousMapId =
          !previousValue.mapId || previousValue.mapId === ""
            ? res.data.id
            : previousValue.mapId;
        if (previousMapId === res.data.id) {
          navigate(`/map/${res.data.id}`, {
            replace: true,
          });
          dispatch(
            setSavedMap({
              isMapSaved: true,
              savedMapName: res.data.name,
              savedMapFolderName: payload.folderName,
            })
          );
          return {
            ...previousValue,
            folderId: res.data.folderId,
            mapId: previousMapId,
            mapName: res.data.name,
            folderName: payload.folderName,
          };
        }
        return { ...previousValue };
      });
    } catch (e) {
      const error = DisplayErrorMessages(e);
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: error,
          showDialog: true,
          timer: "5000",
        })
      );
    } finally {
      dispatch(setSaveMapInstance(false));
      dispatch(setSaveAsMapInstance(false));
      dispatch(setMapSaving(false));
      dispatch(
        showLoader({
          showLoader: false,
          showBackDrop: false,
        })
      );
    }
  };

  const addCrimeRiskSource = () => {
    mapRef.current.addSource("usa-blocks", {
      type: "vector",
      tiles: [MapSourceLayer[activeLayerRef.current.name].path],
      minzoom: 0,
      maxzoom: 14,
    });
  };

  const updateCrimeRiskLayer = () => {
    if (!activeTypeRef.current.type) {
      mapRef.current.removeLayer("usa-blocks-fill");
      return;
    }
    if (mapRef.current.getLayer("usa-blocks-fill")) {
      mapRef.current.removeLayer("usa-blocks-fill");
    }

    const d = activeLayerRef.current.colorScheme;
    const convertedFillColor = [d[d.length - 1].color];
    for (let i = d.length - 2; i >= 0; --i) {
      convertedFillColor.push(Number(d[i].value), d[i].color);
    }
    const targetBelow = mapRef.current
      .getStyle()
      .layers.find(
        (l) => l.id.startsWith("road") || l.id.includes("highway")
      )?.id;
    mapRef.current.addLayer(
      {
        id: "usa-blocks-fill",
        type: "fill",
        source: "usa-blocks",
        "source-layer": MapSourceLayer[activeLayerRef.current.name].sourceLayer,
        paint: {
          "fill-color": [
            "step",
            [
              "to-number",
              [
                "get",
                `${
                  MapSourceLayer[activeLayerRef.current.name]
                    .layerFilterInitialStr
                }_${activeTypeRef.current.type}`,
              ],
            ],
            ...convertedFillColor,
          ],
          "fill-opacity": activeLayerRef.current.opacity,
        },
      },
      targetBelow
    );
  };

  const zoomLevelManage = () => {
    let layerToActivate = "blocks";
    if (countryNameRef.current === "USA") {
      if (activeTypeRef.current.state === "env") {
        layerToActivate =
          mapRef.current.getZoom() < 8 ? "envContinents" : "envBlockGroups";
      } else {
        layerToActivate =
          mapRef.current.getZoom() < 8
            ? "continents"
            : mapRef.current.getZoom() <= 13
            ? "blockGroups"
            : "blocks";
      }
    } else if (countryNameRef.current === "CANADA") {
      layerToActivate = "DA";
      if (activeTypeRef.current.state !== "env") {
        layerToActivate = mapRef.current.getZoom() < 8 ? "FS" : "DA";
      }
    }

    if (activeLayerRef.current.name !== layerToActivate) {
      activeLayerRef.current.name = layerToActivate;
      mapRef.current.removeLayer("usa-blocks-fill");
      mapRef.current.removeSource("usa-blocks");
      addCrimeRiskSource();
      updateCrimeRiskLayer();
    }
  };

  // --------------------- EXTRA COMPONENTS ---------------------
  const drawHighlight = () => {
    const { x, y, width, height } = exportSettingsData;

    return (
      <div
        ref={highlightAreaRef}
        style={{
          position: "absolute",
          top: y,
          left: x,
          width: width,
          height: height,
          border: `2px solid ${palette.primary.main}`,
          backgroundColor: `rgb(2 23 36 / 20%)`,
          pointerEvents: "none",
        }}
      />
    );
  };

  // --------------------- USE EFFECTS ---------------------

  // Sync countryNameRef with countryToggle for users who can toggle
  useEffect(() => {
    if (canToggle) {
      countryNameRef.current = countryToggle ? "USA" : "CANADA";
      console.log("countryNameRef synced:", countryNameRef.current);
    }
  }, [canToggle, countryToggle]);

  useEffect(() => {
    if (mapRef.current && mapRef.current.loaded()) {
      mapRef.current.setStyle(`mapbox://styles/mapbox/${activeMapStyle}`);
      mapRef.current.once("styledata", () => {
        getMapStyle(mapState.layers.selectedLayers);
      });
      const showHighwayStyle =
        mapState.layers.selectedLayers.includes("highway-layer");
      mapRef.current.once("style.load", () => {
        AddHighWayLayer({
          mapRef: mapRef.current,
          highwayStyle: highwayStyleSettings,
          visibility: showHighwayStyle,
        });

        if (["satellite-streets-v12"].includes(mapState.mapStyle)) {
          dispatch(setSatelliteOption(true));
          mapRef.current.setPaintProperty(
            "satellite",
            "raster-opacity",
            mapState.opacityValue
          );
          mapRef.current.setPaintProperty(
            "background",
            "background-color",
            "#fff"
          );
        } else {
          dispatch(setSatelliteOption(false));
        }
        addCrimeRiskSource();
        updateCrimeRiskLayer();
      });
    }
  }, [activeMapStyle]);

  const handleShadingSettings = (isToggle) => {
    if (mapRef.current?.loaded()) {
      activeLayerRef.current.colorScheme =
        shadingSettings.legendColorSchemeData;
      activeLayerRef.current.opacity = shadingSettings.opacity;
      const data = mapState.activeCrimeType
        ? { state: "crime", type: mapState.activeCrimeType }
        : { state: "env", type: mapState.activeEnvType };
      const beforeVal = countryNameRef.current;
      countryNameRef.current = isToggle ? "USA" : "CANADA";
      if (
        activeTypeRef.current.state !== data.state ||
        // activeTypeRef.current.type !== data.type ||
        beforeVal !== countryNameRef.current
      ) {
        activeTypeRef.current = data;
        zoomLevelManage();
      } else {
        activeTypeRef.current = data;
        updateCrimeRiskLayer();
      }
    }
  };

  const handleCountryToggle = (val) => {
    setCountryToggle(val);
    handleShadingSettings(val);
  };

  useEffect(() => {
    handleShadingSettings(countryToggle);
  }, [
    mapState.activeCrimeType,
    mapState.activeEnvType,
    shadingSettings,
    countryToggle,
  ]);

  useEffect(() => {
    if (mapCoordinates.lat !== 0 && mapCoordinates.lng !== 0) {
      openSettingsMenu(mapCoordinates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCoordinates]);

  useEffect(() => {
    (async () => {
      const keys = await caches.keys();
      for (const key of keys) {
        await caches.delete(key);
      }
    })();

    dispatch(getAgsReportList());

    // Determine initial map center and bounds based on selected countries
    let initialCenter = centerOfUSA;
    let initialBounds = RestrictedMapBbox;
    let initialZoomLevel = 0;

    if (canToggle) {
      // Both countries available - use combined bounds but center on Canada
      initialCenter = centerOfMap;
      initialBounds = BothCountriesMapBbox;
      initialZoomLevel = 4;
    } else if (hasCanada && !hasUSA) {
      // Only Canada available
      initialCenter = centerOfCanada;
      initialBounds = CanadaRestrictedMapBbox;
    }
    // If hasUSA only, use default USA settings (already set above)

    mapRef.current = new mapboxgl.Map({
      container: "map",
      center: initialCenter,
      zoom: initialZoomLevel,
      minZoom: 0,
      maxZoom: 14,
      preserveDrawingBuffer: true,
      style: `mapbox://styles/mapbox/${activeMapStyle}`,
      maxBounds: initialBounds,
      projection: "mercator",
      attributionControl: false,
      renderWorldCopies: false,
      //   config: {
      //     basemap: {
      //       lightPreset: "night",
      //     },
      //   },
    });
    mapRef.current.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: false }),
      "bottom-right"
    );

    mapRef.current.on("click", handleMapClickEvent);
    mapRef.current.on("touchend", handleMapClickEvent);

    mapRef.current.once("styledata", () => {
      getMapStyle([]);
    });
    mapRef.current.once("style.load", () => {
      AddHighWayLayer({
        mapRef: mapRef.current,
        highwayStyle: highwayStyleSettings,
      });
    });
    mapRef.current.on("load", () => {
      if (params?.id) {
        getCurrentMapData(params.id);
      }

      setMapFeatureLayerState(JSON.parse(JSON.stringify(mapFeatureLayer)));
      setMapRoadLayerState(JSON.parse(JSON.stringify(mapRoadLayer)));
      addCrimeRiskSource();
      updateCrimeRiskLayer();
    });

    mapRef.current.on("zoomend", zoomLevelManage);

    mapRef.current.on("moveend", () => {
      const currentZoomLevel = mapRef.current.getZoom();
      mapRef.current.once("idle", () => {
        setPOICategoryList((previousValue) => ({
          ...previousValue,
          triggerPoiCheck: previousValue.triggerPoiCheck + 1,
          triggerPoiLayerCheck: previousValue.triggerPoiLayerCheck + 1,
          currentZoomLevel: currentZoomLevel,
          isZoom: true,
        }));
      });
      // }
    });
    setMapRState(mapRef.current);

    return () => {
      dispatch(clearTradeArea());
      dispatch(resetMapSettings());
      mapRef.current.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Not show POI image when map in export mode
    if (mapRef.current && !mapLayoutState) {
      getChangedLocationImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [POICategoryList.triggerPoiCheck]);

  useEffect(() => {
    if (
      (Object.keys(POICategoryList.selectedPOIRetailerNameList).length > 0 ||
        POICategoryList.selectedAsLayer.length > 0) &&
      POICategoryList.triggerGetPoi
    ) {
      const getBound = mapRef.current.getBounds();
      sendWebSocketMessage("get-poi", {
        data: {
          retailerId: Object.keys(POICategoryList.selectedPOIRetailerNameList),
          bounding_box: {
            top_left_lat: getBound.getNorthWest().lat,
            top_left_long: getBound.getNorthWest().lng,
            bottom_right_lat: getBound.getSouthEast().lat,
            bottom_right_long: getBound.getSouthEast().lng,
          },
        },
      });
    } else {
      setPOICategoryList((prev) => ({ ...prev, triggerGetPoi: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [POICategoryList.triggerGetPoi]);

  useEffect(() => {
    // Not show POI image when map in export mode
    if (mapRef.current && !mapLayoutState) {
      CustomPOIClass.RemovePoiImageOutOfBound({
        POICategoryList,
        map: mapRef.current,
        mapDraw: drawRef,
        mapboxgl,
        setPOICategoryList,
        lastClickButton: lastClickButtonRef,
        logoDefaultStyle: logoStyleSettings,
        defaultLogoValue: defaultLogoValueRef,
        poiSetting: poiStyleSettings,
        setSelectedLogo,
        setMapToolBarOptionFlags,
        setResetMap: () => {},
        createNewPolygon: createPolygon,
        updateAutoSave: () => {},
        resetAllOnceChanged,
        currentMode: currentModeRef,
        polygonDefaultStyle: CustomPolygonClass.polygonDefaultStyle,
        getCurrentPoiSetting,
        setDrawnFeaturesSupport: () => {},
        setUndoHistory: () => {},
        setRedoHistory: () => {},
        markerRef,
      });
      setMarkers(markerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [POICategoryList.triggerPoiRemove]);

  useEffect(() => {
    if (params.id && mapState.saveMap) {
      saveMapInstanceToAPI({
        mapName: userMap.mapName,
        folderId: userMap.folderId,
        mapId: params.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapState.saveMap]);

  useEffect(() => {
    if (mapRef.current && mapRef.current.loaded()) {
      if (["satellite-streets-v12"].includes(mapState.mapStyle)) {
        dispatch(setSatelliteOption(true));
        mapRef.current.setPaintProperty(
          "satellite",
          "raster-opacity",
          mapState.opacityValue
        );
        mapRef.current.setPaintProperty(
          "background",
          "background-color",
          "#fff"
        );
        // updateAutoSave();
      } else {
        dispatch(setSatelliteOption(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapState.opacityValue]);

  // useEffect(() => {
  //   activeLayerRef.current.colorScheme = shadingSettings.legendColorSchemeData;
  //   updateCrimeRiskLayer();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [shadingSettings.legendColorSchemeData]);

  return (
    <Box sx={{ display: "flex" }}>
      {((!params.id && mapState.saveMap) || mapState.saveAsMap) && (
        <SaveMapModal
          open={(!params.id && mapState.saveMap) || mapState.saveAsMap}
          onSave={saveMapInstanceToAPI}
        />
      )}
      <MapLayout
        setCountryToggle={handleCountryToggle}
        countryToggle={countryToggle}
        hasCanada={hasCanada}
        canToggleCountry={canToggle}
        mapLayoutState={mapLayoutState}
        mapRef={mapRef.current}
        setMapLayout={(val) => {
          mapActionsRef.current = val;
          setMapLayoutState(val);
        }}
        mapboxglRef={mapboxgl}
        mapRoadLayer={mapRoadLayerState}
        mapFeatureLayer={mapFeatureLayerState}
        exportSettingsData={exportSettingsData}
        setExportSettingsData={setExportSettingsData}
        exportChanges={exportChanges}
        markers={markers}
        setMarkers={setMarkers}
        lastClickButtonRef={lastClickButtonRef}
        resetAllOnceChanged={resetAllOnceChanged}
        resetActiveId={resetActiveId}
        // POICategoryList={POICategoryList}
        // setPOICategoryList={setPOICategoryList}
        sendWebSocketMessage={sendWebSocketMessage}
        markerRef={markerRef}
        setSelectedLogo={setSelectedLogo}
        showSaveOption={Boolean(params.id)}
        setShowOpacityControl={setShowOpacityControl}
        activeLayerRef={activeLayerRef}
        setPolygonSelected={setPolygonSelected}
        createPolygon={createPolygon}
        setMapToolBarOptionFlags={setMapToolBarOptionFlags}
        resetMapToolbarOptionFlags={resetMapToolbarOptionFlags}
        setShapeSettings={setShapeSettings}
        isClusterCallOutRef={isClusterCallOutRef}
      />
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
        }}
      >
        <div
          ref={mapWrapperRef}
          style={{
            position: "relative",
            height: "100%",
            width: "100%",
            // overflow: 'hidden'
          }}
          // className="App"
        >
          <CrimeRiskLegend
            showLegend={mapState.activeCrimeType || mapState.activeEnvType}
            leagendRef={leagendRef}
          />
          <div
            style={{
              height: "100vh",
              width: "100%",
              minWidth: themeWidth["1400px"],
            }}
            id="map"
          />
        </div>
        {mapLayoutState ? drawHighlight() : null}
      </div>
      {!mapLayoutState && (
        <>
          <HighwaySetting
            mapRef={mapRef.current}
            highwayStyleSettings={highwayStyleSettings}
          />
          <MapHeader />
          {/* <MapStyleSetting
            mapRef={mapRef.current}
            setShowOpacityControl={setShowOpacityControl}
            resetAllOnceChanged={resetAllOnceChanged}
          /> */}
          {showOpacityControl && (
            <BulbSetting setShowOpacityControl={setShowOpacityControl} />
          )}
          {mapState.showPoiSettings && (
            <AllPOIStyleSettings
              mapRef={mapRef.current}
              drawRef={drawRef.current}
              setMarkers={setMarkers}
              markerRef={markerRef}
              // updateAutoSave={updateAutoSave}
              // setHighwaySelected={setHighwaySelected}
              // setOpenRingRadiusSetting={setRingRadiusSetting}
            />
          )}
          {/* <MapToolsBox
            mapRef={mapRef.current}
            drawRef={drawRef}
            // showPolygonStyleSettings={() => setShowPolygonStyleSettings(true)}
            setPolygonSelected={setPolygonSelected}
            createPolygon={createPolygon}
            setMapToolBarOptionFlags={setMapToolBarOptionFlags}
            resetActiveId={resetActiveId}
            resetMapToolbarOptionFlags={resetMapToolbarOptionFlags}
            setShapeSettings={setShapeSettings}
            resetAllOnceChanged={resetAllOnceChanged}
            isClusterCallOutRef={isClusterCallOutRef}
          /> */}
          {activeMarker && (
            <MarkerStyleSetting
              markers={markers}
              setMarkers={setMarkers}
              markerRef={markerRef}
              mapRef={mapRef.current}
              createTextBox={createTextBox}
              drawRef={drawRef}
              setTextBox={setTextBox}
              isEnabledActionRef={isEnabledActionRef}
            />
          )}
          {mapToolBarOptionFlags.polygon && polygonSelected && (
            <PolygonStyleSetting
              mapRef={mapRef.current}
              drawRef={drawRef.current}
              polygonSelected={polygonSelected}
              setPolygonSelected={setPolygonSelected}
              currentModeRef={currentModeRef}
              markers={markers}
              setMarkers={setMarkers}
              resetAllOnceChanged={resetPolygonDrawOnceChanged}
              lastClickButtonRef={lastClickButtonRef}
              resetActiveId={resetActiveId}
              markerRef={markerRef}
            />
          )}
          {shapeSettings.selectedShape && (
            <ShapeStyleSetting
              mapRef={mapRef.current}
              setShapeSettings={setShapeSettings}
              setMarkers={setMarkers}
              resetAllOnceChanged={resetPolygonDrawOnceChanged}
              shapeSettings={shapeSettings}
              markerRef={markerRef}
            />
          )}
          {textBox.selectedTextBox && (
            <TextStyleSetting
              mapRef={mapRef.current}
              drawRef={drawRef.current}
              textBoxSetttings={textBox.selectedTextBox}
              // setTextSelected={setTextSelected}
              setTextBox={setTextBox}
              currentModeRef={currentModeRef}
              resetAllOnceChanged={resetAllOnceChanged}
              markers={markers}
              setMarkers={setMarkers}
              markerRef={markerRef}
            />
          )}
          {showLogoSetting && (
            <LogoSetting
              mapRef={mapRef.current}
              drawRef={drawRef}
              imageCoordinates={mapCoordinates}
              lastClickButtonRef={lastClickButtonRef}
              setMapToolBarOptionFlags={setMapToolBarOptionFlags}
              setMarkers={setMarkers}
              createNewPolygon={createPolygon}
              handleClose={handleLogoSettingClose}
              currentMode={currentModeRef}
              resetAllOnceChanged={resetAllOnceChanged}
              getCurrentPoiSetting={getCurrentPoiSetting}
              disableUserActions={disableUserActions}
              setSelectedLogo={setSelectedLogo}
              defaultLogoValue={defaultLogoValueRef}
              markerRef={markerRef}
            />
          )}
          {selectedLogoSettings?.openSetting && (
            <LogoStyleSetting
              mapRef={mapRef.current}
              drawRef={drawRef}
              resetAllOnceChanged={resetAllOnceChanged}
              setMarkers={setMarkers}
              selectedLogo={selectedLogoSettings}
              setSelectedLogo={setSelectedLogo}
              markers={markers}
              currentMode={currentModeRef}
              lastClickButtonRef={lastClickButtonRef}
              defaultLogoValue={defaultLogoValueRef}
              setMapToolBarOptionFlags={setMapToolBarOptionFlags}
              POICategoryList={POICategoryList}
              setPOICategoryList={setPOICategoryList}
              createNewPolygon={createPolygon}
              getCurrentPoiSetting={getCurrentPoiSetting}
              markerRef={markerRef}
              deletedPoiRef={deletedPoiRef}
            />
          )}
          {activeId === "shape" && (
            <ShapeSelector
              mapRef={mapRef.current}
              handleClose={resetActiveId}
              setShapeSettings={setShapeSettings}
            />
          )}
          {activeAgsReport?.selected &&
            (activeAgsReport.type === "polygon-trade-area" ||
              activeAgsReport.radiusEnable) &&
            !activeMarker && (
              <AgsReport
                mapRef={mapRef.current}
                markerRef={markerRef.current}
                mapDraw={drawRef.current}
                close={() => {
                  dispatch(setShowAgsReport(null));
                }}
              />
            )}
        </>
      )}
    </Box>
  );
};

export default BuildAMap;
